import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "../components/ExistingCheckIn.css";
import LoadingSkeleton from "components/LoadingSkeleton";
import ExistingCausesList from "../components/ExistingCausesList";
import CauseSelector from "../components/CauseSelector";
import { useAppState } from "../../../contexts/UserContext";
import { useAuth } from "../../../contexts/AuthContext";
import { useCause } from "../../../contexts/CauseContext";
import { useTeam } from "../../../contexts/TeamContext";
import useDurationManagement from "../hooks/useDurationManagement";
import { useCheckIn } from "../checkin-context";
import useCauseProcessor from "../../../hooks/useCauseProcessor";
import { rewards } from "../rewards";
import styles from "../CheckIn.module.css";
import { useCheckInSavedCauses } from "../hooks/useCheckInSavedCauses";
import { useCheckInCausesToOfferLoader } from "../hooks/useCheckInCausesToOfferLoader";
import { useCheckInEnMoodSelection } from "../hooks/useCheckInEnMoodSelection";
import {
  useCheckInViewHandlers,
  type CheckInCauseLike,
} from "../hooks/useCheckInViewHandlers";
import type { ExistingRecord } from "../checkin-context";
import type { Dispatch, SetStateAction } from "react";

export type CheckInLocationState = {
  imageIndex?: number | null;
  moodName?: string;
  enableDelete?: boolean;
  savedCauses?: unknown[] | null;
  duration?: number;
  [key: string]: unknown;
};

export type CheckInViewProps = {
  stateOverride?: CheckInLocationState;
  onClose?: () => void;
};

type DurationManagementResult = {
  duration: number | null;
  getTotalDurationForToday: () => number;
  getPreviousDurationForCurrentSelection: () => number;
  getAvailableDurationOptions: () => { label: string; duration: number }[];
  wouldExceedDailyLimit: (
    durationValue: number,
    previousDurationOverride?: number | null
  ) => boolean;
};

const CheckInView = ({ stateOverride, onClose }: CheckInViewProps = {}) => {
  const {
    moods,
    setMoods,
    refreshTeamMoods,
    clearCache,
    themeId,
    collectionId,
  } = useAppState() as {
    moods: unknown[] | null | undefined;
    setMoods: (v: unknown) => void;
    refreshTeamMoods?: () => void;
    clearCache?: (key: string) => void;
    themeId: unknown;
    collectionId: unknown;
  };
  const { userId } = useAuth();
  const { organizationId, teamId } = useTeam();
  const { causes, setCauses, causeTypes, defaultCauseTypes, activeCauseTypes } =
    useCause() as {
      causes: unknown[];
      setCauses: (v: unknown) => void;
      causeTypes: { id: string; [key: string]: unknown }[];
      defaultCauseTypes: unknown[] | null | undefined;
      activeCauseTypes: { id: string; archived?: boolean }[] | null | undefined;
    };
  const { activeTeamCauseTypes } = useTeam() as {
    activeTeamCauseTypes:
      | { id: string; archived?: boolean }[]
      | null
      | undefined;
  };

  const navigate = useNavigate();
  const { t } = useTranslation();
  const location = useLocation();
  const mergedState = (stateOverride ??
    (location.state as CheckInLocationState | null) ??
    {}) as CheckInLocationState;

  const {
    selectedCauses,
    causesToOffer,
    feelingName,
    selectedDate,
    existingRecord,
    selectCause,
    clearCauses,
    setCausesToOffer,
    setCausesLoading,
  } = useCheckIn();

  const { getCausesForMood, enrichCausesWithTranslations } =
    useCauseProcessor() as {
      getCausesForMood: (id: string) => unknown[];
      enrichCausesWithTranslations: (
        specs: unknown[],
        useAll: boolean
      ) => unknown[];
    };

  const moodName = mergedState.moodName ?? "";

  const [, setRewardIdx] = useState<number | null>(null);
  const [yourOwnCause, setYourOwnCause] = useState("");
  const [, setYourOwnCauseTypeId] = useState("");
  const [displayedText, setDisplayedText] = useState(() => {
    const enableDelete = mergedState.enableDelete;
    return enableDelete ? "" : `${t("homepage.whatDuration")} ${moodName}?`;
  });

  const [customDate, setCustomDate] = useState("");
  const isExisting = Boolean(existingRecord);
  const [selectedDuration, setSelectedDuration] = useState(
    existingRecord?.duration ?? 1
  );
  const [selectedCause, setSelectedCause] = useState<unknown>(null);
  const [newMood, setNewMood] = useState<unknown>(null);
  const [causeValueToSave, setCauseValueToSave] = useState<unknown>(null);
  const [updateCauses] = useState(false);
  const [step, setStep] = useState("cause");
  const [update, setUpdate] = useState(false);

  const { savedCauses, setSavedCauses } = useCheckInSavedCauses({
    existingRecord: existingRecord as ExistingRecord | null | undefined,
    initialFromState: mergedState.savedCauses,
    getCausesForMood,
    enrichCausesWithTranslations,
  });

  useCheckInCausesToOfferLoader({
    causes: causes as unknown[],
    activeCauseTypes: activeCauseTypes ?? [],
    activeTeamCauseTypes: activeTeamCauseTypes ?? [],
    defaultCauseTypes: defaultCauseTypes as { archived?: boolean }[] | null,
    feelingName,
    selectedDate,
    t,
    setCausesToOffer: setCausesToOffer as (c: unknown[]) => void,
    setCausesLoading,
    updateCauses,
  });

  const { feelingNames, selectingMood, setSelectingMood, setRelevantMoods } =
    useCheckInEnMoodSelection({
      theme: themeId,
      collection: collectionId,
      t,
      feelingName,
      mergedImageIndex: mergedState.imageIndex,
    });

  const isReady =
    Boolean(userId) &&
    selectedDate != null &&
    feelingName != null &&
    (!isExisting || savedCauses != null);

  const { duration } = useDurationManagement({
    location,
    moods: moods ?? [],
    selectedDate: selectedDate ?? "",
    userId: userId ?? "",
    feelingName: feelingName ?? "",
  }) as DurationManagementResult;

  const {
    handleCauseSelect,
    handleCauseDelete,
    onCauseFlowFinish,
    onDeleteMoodFromCauseSelector,
    onDeleteMoodFooter,
  } = useCheckInViewHandlers({
    userId,
    organizationId,
    teamId,
    feelingName,
    selectedDate,
    causes: causes as unknown[],
    selectedCauses: selectedCauses as CheckInCauseLike[],
    duration,
    selectedDuration,
    existingRecord,
    setMoods: setMoods as Dispatch<
      SetStateAction<unknown[] | null | undefined>
    >,
    clearCache,
    refreshTeamMoods,
    onClose,
    clearCauses,
    selectCause: selectCause as (c: CheckInCauseLike) => void,
    setCausesToOffer: setCausesToOffer as (c: CheckInCauseLike[]) => void,
    causesToOffer: causesToOffer as CheckInCauseLike[],
    setSavedCauses,
    activeCauseTypes: activeCauseTypes ?? [],
    causeTypes,
    step,
    moods,
    selectingMood,
    feelingNames,
    customDate,
    setRelevantMoods,
    setSelectingMood,
    setDisplayedText,
    navigate,
    setCauses: setCauses as Dispatch<SetStateAction<unknown[]>>,
    setStep,
    setRewardIdx,
    rewardsLength: rewards.length,
  });

  const addNewCause = () => {
    setUpdate(true);
  };

  return (
    <>
      {!isReady && <LoadingSkeleton variant="grid" />}

      <p className={styles["duration-text"]}>
        {/*whatDurationText} {step*/}
        Experience of anxiety
      </p>

      {!update && (
        <>
          {!isReady ? (
            <LoadingSkeleton variant="column" />
          ) : (
            <ExistingCausesList
              savedCauses={savedCauses}
              handleCauseDelete={handleCauseDelete}
            />
          )}

          <div style={{ display: "flex" }}>
            <div onClick={() => addNewCause()} className="add-new-cause-button">
              {/*t('add.newCause')*/}
              Add cause
            </div>
          </div>
        </>
      )}

      {update && (
        <div className="update-causes-container">
          <p className="bold-text">{displayedText}</p>

          <CauseSelector
            userId={userId}
            causesToOffer={causesToOffer}
            selectedCauses={selectedCauses}
            existingRecord={existingRecord}
            navigate={navigate}
            handleCauseSelect={handleCauseSelect}
            selectedCause={selectedCause}
            selectedDate={selectedDate}
            yourOwnCause={yourOwnCause}
            setYourOwnCause={setYourOwnCause}
            setYourOwnCauseTypeId={setYourOwnCauseTypeId}
            onFinish={onCauseFlowFinish}
            onDelete={onDeleteMoodFromCauseSelector}
          />
        </div>
      )}
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginTop: "24px",
        }}
      >
        <p
          style={{
            margin: 0,
            color: "#d32f2f",
            cursor: "pointer",
          }}
          onClick={onDeleteMoodFooter}
        >
          delete
        </p>
      </div>
    </>
  );
};

export default CheckInView;
